import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button, BulkSelectCell, TableSkeleton,
  RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton,
  CustomSelect, StatusDot, Amount, MoreMenu, btnPrimary, btnDisabled,
} from '@redpanda/ui';
import {
  useBulkActions, createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler, createOptimisticBulkDeleteUpdate,
  isAdmin, formatTableDate, useRefetchOnFocus, useRememberedState,
} from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import InvoiceDialog from '../components/InvoiceDialog';
import InvoicePaymentDialog from '../components/InvoicePaymentDialog';
import CreditNoteDialog from '../components/CreditNoteDialog';
import InvoiceLimitBanner from '../components/InvoiceLimitBanner';
import { useInvoiceLimitCache } from '../hooks/useInvoiceLimitCache';
import { useListSearch } from '../hooks/useListSearch';
import {
  Invoice, InvoiceStatus, INVOICE_STATUSES, displayStatus, hasSettlements, isInvoiceDeletable, outstandingOf, updateInvoiceInLists,
  useInvoiceStoreBase, useArchivedInvoiceStoreBase,
} from '../stores/invoiceStore';
import { bulkDeleteInvoices, getInvoices, setInvoicesArchived, updateInvoiceStatus } from '../services/api';
import { notifyDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { invoiceTone, toneOf } from '../utils/statusTone';
import { apiErrorMessage } from '../utils/apiError';
import { yearOptions } from '../stores/reportStore';

const COLUMNS = 7;
type StatusFilter = InvoiceStatus | 'ALL';
const STATUS_FILTERS: readonly StatusFilter[] = ['ALL', ...INVOICE_STATUSES];

/**
 * 發票管理／已封存發票（照 HR 員工列表：後端搜尋、批次刪除／封存、方案每月發票數）
 * 狀態流程：草稿 →（開立：自動記帳）已開立 →（登記收款，可分次；收滿）已收款；已開立過了到期日顯示逾期；已記帳的發票不能刪，要作廢（同 Xero、QuickBooks）
 */
const InvoiceManagePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isArchived = location.pathname === '/invoices/archived';
  const { user } = useContext(UserContext)!;
  const userRole = user?.role;
  const admin = isAdmin(userRole);

  const storeBase = isArchived ? useArchivedInvoiceStoreBase : useInvoiceStoreBase;
  const store = storeBase();
  const { items: invoices, pagination, isLoading, isPageLoading, showDeleteConfirmModal, isDeleting, fetchItems, setCurrentPage } = store;
  const { canCreateInvoice, setCanCreateInvoice, limitBannerRefreshTrigger, setLimitBannerRefreshTrigger } = useInvoiceLimitCache();
  const refreshLimit = () => setLimitBannerRefreshTrigger(prev => prev + 1);

  // 搜尋、年月不記住；狀態篩選記住（R50）；月份要先選年份
  const [searchTerm, setSearchTerm] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [statusFilter, setStatusFilter] = useRememberedState<StatusFilter>('accounting-ui:invoices.status', 'ALL', STATUS_FILTERS);
  const [dialogInvoice, setDialogInvoice] = useState<Invoice | null | undefined>(undefined); // undefined＝關閉、null＝新增
  const [statusChange, setStatusChange] = useState<{ invoice: Invoice; status: InvoiceStatus } | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);
  const [creditInvoice, setCreditInvoice] = useState<Invoice | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);

  useEffect(() => {
    setSearchTerm('');
    setYear('');
    setMonth('');
  }, [isArchived]);

  useEffect(() => {
    fetchItems(1);
  }, [fetchItems, isArchived]);
  useRefetchOnFocus(() => storeBase.getState().refreshData());

  const search = searchTerm.trim();
  const status = statusFilter === 'ALL' ? undefined : statusFilter;
  const hasFilters = !!(search || year || status);
  const query = { archived: isArchived, search: search || undefined, year: year || undefined, month: (year && month) || undefined, status };
  const searchResult = useListSearch<Invoice>('invoices', hasFilters ? query : null, page => getInvoices(page, 20, query));
  const listItems = searchResult.active ? searchResult.items : invoices;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const canDelete = (invoice: Invoice) => admin && isInvoiceDeletable(invoice);

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => storeBase.getState(), fetchItems, setCurrentPage);
  const bulkActions = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('invoices.item'),
    bulkDeleteApi: bulkDeleteInvoices,
    canDeleteItem: () => admin,
    userRole,
    t,
    toast,
    pagination: listPagination ? { currentPage: listPagination.currentPage, totalPages: listPagination.totalPages } : undefined,
    fetchItems,
    setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(storeBase.setState, isArchived ? 'archivedInvoice' : 'invoice', () => invoices, () => pagination),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      refreshLimit();
      notifyDataChanged('invoices');
    },
  });
  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));
  const selectedPaid = listItems.some(invoice => bulkActions.selectedItems.includes(invoice.id) && !isInvoiceDeletable(invoice));

  // 封存／恢復：從目前列表移到另一個列表（照 HR 員工）
  const moveInvoices = async (archived: boolean) => {
    const ids = bulkActions.selectedItems;
    const sourceBase = archived ? useInvoiceStoreBase : useArchivedInvoiceStoreBase;
    const targetBase = archived ? useArchivedInvoiceStoreBase : useInvoiceStoreBase;
    const moved = listItems.filter(invoice => ids.includes(invoice.id)).map(invoice => ({ ...invoice, archived }));
    createOptimisticBulkDeleteUpdate(sourceBase.setState, archived ? 'invoice' : 'archivedInvoice')(ids, invoices, pagination);
    bulkActions.setSelectedItems([]);
    try {
      const { updatedCount } = (await setInvoicesArchived(ids, archived)).data;
      toast.success(t(archived ? 'invoices.list.archiveSuccess' : 'invoices.list.restoreSuccess', { count: updatedCount }));
      const target = targetBase.getState();
      const existing = new Set(target.items.map(item => item.id));
      const added = moved.filter(invoice => !existing.has(invoice.id));
      if (added.length > 0) {
        targetBase.setState({
          items: [...added, ...target.items],
          pagination: target.pagination ? { ...target.pagination, totalItems: target.pagination.totalItems + added.length } : null,
        });
      }
      refreshLimit();
      notifyDataChanged('invoices');
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
    await sourceBase.getState().refreshData();
  };

  /** 改狀態：等伺服器回應（儀表板、應收帳款要跟著變） */
  const applyStatus = async (invoice: Invoice, next: InvoiceStatus) => {
    setChangingStatus(true);
    try {
      const { data } = await updateInvoiceStatus(invoice.id, next);
      updateInvoiceInLists(invoice.id, data);
      toast.success(t('invoices.list.statusChanged', { status: t(`invoices.status.${next}`) }));
      notifyDataChanged('invoices');
      notifyDataChanged('journals'); // 開立、改回草稿、作廢都會自動記帳
    } catch (error) {
      toast.error(apiErrorMessage(error, 'common.messages.updateFailed'));
    } finally {
      setChangingStatus(false);
      setStatusChange(null);
    }
  };

  const handleConfirmDelete = async () => {
    await store.confirmDelete();
    refreshLimit();
    notifyDataChanged('invoices');
  };

  const addButton = (
    <button
      type="button"
      onClick={() => canCreateInvoice && setDialogInvoice(null)}
      disabled={!canCreateInvoice}
      className={canCreateInvoice ? btnPrimary : btnDisabled}
      title={canCreateInvoice ? undefined : t('errors.INVOICE_LIMIT_REACHED')}
    >
      {t('invoices.list.addButton')}
    </button>
  );

  /** 每列的動作：最多兩個文字動作，其他收進「⋯」（§3-5） */
  const rowActions = (invoice: Invoice) => {
    if (isArchived) {
      return admin ? (
        <RowActions>
          <RowAction onClick={() => setDialogInvoice(invoice)}>{t('common.actions.view')}</RowAction>
        </RowActions>
      ) : null;
    }
    const viewOnly = invoice.status === 'CANCELLED';
    const open = invoice.status === 'SENT' || invoice.status === 'OVERDUE';
    const hasPayments = hasSettlements(invoice);
    const more = [
      ...(open && !hasPayments ? [{ key: 'draft', label: t('invoices.actions.backToDraft'), onClick: () => applyStatus(invoice, 'DRAFT') }] : []),
      // 上線前手動標記「已收款」、沒有收款記錄的發票可以改回已開立（之後用登記收款）
      ...(invoice.status === 'PAID' && !hasPayments ? [{ key: 'unpaid', label: t('invoices.actions.markUnpaid'), onClick: () => applyStatus(invoice, 'SENT') }] : []),
      // 折讓：已開立、逾期、已收款都可以（超過未收的部分退款）
      ...(open || invoice.status === 'PAID' ? [{ key: 'credit', label: t('invoices.actions.creditNote'), onClick: () => setCreditInvoice(invoice) }] : []),
      ...(invoice.status !== 'CANCELLED' && invoice.status !== 'PAID' && !hasPayments
        ? [{ key: 'cancel', label: t('invoices.actions.cancel'), onClick: () => setStatusChange({ invoice, status: 'CANCELLED' }) }] : []),
      ...(canDelete(invoice) ? [{ key: 'delete', label: t('common.actions.delete'), tone: 'danger' as const, onClick: () => store.handleDelete(invoice.id) }] : []),
    ];
    return (
      <RowActions>
        <RowAction onClick={() => setDialogInvoice(invoice)}>{viewOnly ? t('common.actions.view') : t('common.actions.edit')}</RowAction>
        {invoice.status === 'DRAFT' && (
          <RowAction disabled={changingStatus} onClick={() => applyStatus(invoice, 'SENT')}>{t('invoices.actions.issue')}</RowAction>
        )}
        {open && <RowAction onClick={() => setPaymentInvoice(invoice)}>{t('invoices.actions.recordPayment')}</RowAction>}
        {more.length > 0 && <MoreMenu variant="icon" label={t('accounts.list.moreActions')} items={more} />}
      </RowActions>
    );
  };

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {isArchived ? t('invoices.list.archivedTitle') : t('invoices.list.title')}
          </h1>
          <div className="flex space-x-3 whitespace-nowrap">
            {admin && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
                deleteDisabled={selectedPaid}
                deleteDisabledMessage={t('invoices.list.cannotDeletePaid')}
                onArchive={isArchived ? undefined : () => moveInvoices(true)}
                onRestore={isArchived ? () => moveInvoices(false) : undefined}
              />
            )}
            {!isArchived && addButton}
            <Link to={isArchived ? '/invoices' : '/invoices/archived'}>
              <Button variant="secondary">{isArchived ? t('invoices.list.backToList') : t('common.bulk.viewArchivedAction')}</Button>
            </Link>
          </div>
        </div>
      </div>

      {!isArchived && <InvoiceLimitBanner onCanCreateInvoiceChange={setCanCreateInvoice} refreshTrigger={limitBannerRefreshTrigger} />}

      <div className="mb-4 flex flex-col md:flex-row md:flex-wrap gap-3">
        <div className="md:w-80">
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('invoices.list.searchPlaceholder')} />
        </div>
        <div className="md:w-32">
          <CustomSelect
            value={year}
            onChange={value => { setYear(value); if (!value) setMonth(''); }}
            options={[{ value: '', label: t('invoices.list.allYears') }, ...yearOptions(6).map(y => ({ value: String(y), label: t('dashboard.yearLabel', { year: y }) }))]}
          />
        </div>
        <div className="md:w-28">
          <CustomSelect
            value={month}
            onChange={setMonth}
            disabled={!year}
            options={[{ value: '', label: t('invoices.list.allMonths') }, ...Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: t(`common.time.months.${i + 1}`) }))]}
          />
        </div>
        <div className="md:w-36">
          <CustomSelect
            value={statusFilter}
            onChange={value => setStatusFilter(value as StatusFilter)}
            options={STATUS_FILTERS.map(value => ({ value, label: value === 'ALL' ? t('journals.list.allStatus') : t(`invoices.status.${value}`) }))}
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={() => { setSearchTerm(''); setYear(''); setMonth(''); setStatusFilter('ALL'); }}
            className="h-10 text-sm font-medium text-fg-secondary hover:text-fg self-start md:self-auto"
          >
            {t('common.filters.clear')}
          </button>
        )}
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && invoices.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={10} columns={COLUMNS} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '960px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={bulkActions.allSelected}
                    onToggleAll={bulkActions.toggleSelectAll}
                    hasItems={listItems.length > 0}
                  />
                  <TableHead className="w-40">{t('invoices.table.number')}</TableHead>
                  <TableHead>{t('invoices.table.customerName')}</TableHead>
                  <TableHead numeric className="w-28">{t('invoices.table.date')}</TableHead>
                  <TableHead numeric className="w-28">{t('invoices.table.dueDate')}</TableHead>
                  <TableHead numeric className="w-36">{t('invoices.table.amount')}</TableHead>
                  <TableHead className="w-24">{t('invoices.table.status')}</TableHead>
                  <TableHead className="w-36">{t('invoices.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={COLUMNS + 1}
                    filtered={hasFilters}
                    title={isArchived ? t('invoices.list.noArchivedInvoices') : t('invoices.list.noInvoices')}
                    hint={isArchived ? t('invoices.list.archivedHint') : t('invoices.list.emptyHint')}
                    action={!isArchived && canCreateInvoice && addButton}
                  />
                ) : (
                  listItems.map(invoice => {
                    const shown = displayStatus(invoice);
                    return (
                      <TableRow key={invoice.id} className={pendingRowClass(invoice)}>
                        <BulkSelectCell
                          userRole={userRole}
                          isSelected={bulkActions.selectedItems.includes(invoice.id)}
                          onToggle={() => bulkActions.toggleItemSelection(invoice.id)}
                        />
                        <TableCell className="truncate tabular-nums font-medium text-fg" title={invoice.number}>{invoice.number}</TableCell>
                        <TableCell className="truncate" title={invoice.customerName}>
                          {invoice.customerName}
                          {invoice.customerTaxId && <span className="block text-[13px] text-fg-secondary tabular-nums">{invoice.customerTaxId}</span>}
                        </TableCell>
                        <TableCell numeric>{formatTableDate(invoice.date, i18n.language)}</TableCell>
                        <TableCell numeric className={shown === 'OVERDUE' ? '!text-red-600 dark:!text-red-400' : undefined}>
                          {invoice.dueDate ? formatTableDate(invoice.dueDate, i18n.language) : <span className="text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell numeric>
                          <Amount value={Number(invoice.totalAmount)} />
                          {hasSettlements(invoice) && invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && (
                            <span className="block text-[13px] text-fg-secondary">{t('invoices.list.outstanding')} <Amount value={outstandingOf(invoice)} /></span>
                          )}
                        </TableCell>
                        <TableCell><StatusDot tone={toneOf(invoiceTone, shown)} label={t(`invoices.status.${shown}`)} /></TableCell>
                        <TableCell>{rowActions(invoice)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={store.closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('invoices.list.confirmDeleteTitle')}
        message={t('invoices.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('invoices.list.confirmBulkDeleteTitle', { count: bulkActions.selectedItems.length })}
        message={t('invoices.list.confirmBulkDeleteMessage', { count: bulkActions.selectedItems.length })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={!!statusChange}
        onClose={() => setStatusChange(null)}
        onConfirm={() => statusChange && applyStatus(statusChange.invoice, statusChange.status)}
        title={t('invoices.list.confirmCancelTitle')}
        message={statusChange ? t('invoices.list.confirmCancelMessage', { number: statusChange.invoice.number }) : ''}
        confirmText={t('invoices.actions.cancel')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={changingStatus}
        loadingText={t('common.status.updating')}
        t={t}
      />

      {paymentInvoice && <InvoicePaymentDialog invoice={paymentInvoice} onClose={() => setPaymentInvoice(null)} />}
      {creditInvoice && <CreditNoteDialog invoice={creditInvoice} onClose={() => setCreditInvoice(null)} />}

      {dialogInvoice !== undefined && (
        <InvoiceDialog
          invoice={dialogInvoice ?? undefined}
          readOnly={!!dialogInvoice && (isArchived || dialogInvoice.status === 'CANCELLED')}
          onClose={() => setDialogInvoice(undefined)}
          onCreated={refreshLimit}
        />
      )}
    </div>
  );
};

export default InvoiceManagePage;
